import { useOnClickOutside } from "hooks";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "ui";

import { AdminEmailSuggestion } from "../../emailNotifications.types";

import styles from "./EmailAutocomplete.module.scss";

interface EmailAutocompleteProps {
  value: string;
  suggestions: AdminEmailSuggestion[];
  onChange: (email: string) => void;
  onSelect: (suggestion: AdminEmailSuggestion) => void;
  label?: string;
  placeholder?: string;
}

const SUGGESTIONS_LIMIT = 8;

const EmailAutocomplete = ({
  value,
  suggestions,
  onChange,
  onSelect,
  label,
  placeholder
}: EmailAutocompleteProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useOnClickOutside(containerRef, () => setIsOpen(false));

  const filtered = useMemo(() => {
    const query = value.trim().toLowerCase();
    const matches = query
      ? suggestions.filter(
          (suggestion) =>
            suggestion.email.toLowerCase().includes(query) ||
            `${suggestion.firstName} ${suggestion.lastName}`
              .toLowerCase()
              .includes(query)
        )
      : suggestions;

    return matches.slice(0, SUGGESTIONS_LIMIT);
  }, [value, suggestions]);

  return (
    <div
      className={styles.wrap}
      ref={containerRef}
      onClick={() => setIsOpen(true)}
    >
      <Input
        label={label}
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
      />

      {isOpen && filtered.length > 0 && (
        <div className={styles.dropdown}>
          {filtered.map((suggestion) => (
            <button
              key={suggestion.adminId}
              type="button"
              className={styles.option}
              onClick={() => {
                onSelect(suggestion);
                setIsOpen(false);
              }}
            >
              <span className={styles.option__name}>
                {suggestion.firstName} {suggestion.lastName}
                {suggestion.role === "Super" && (
                  <span className={styles.option__badge}>
                    {t("auto-subscribed")}
                  </span>
                )}
              </span>
              <span className={styles.option__email}>{suggestion.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailAutocomplete;
